'use client';

import { c as _c } from "react/compiler-runtime";
import { useQueryClient } from '@tanstack/react-query';
import { type Dispatch, type SetStateAction, useReducer, useState } from 'react';
import { type VolunteerRegistrationPayload, type VolunteerRegistrationResult } from '@sola/schema/dotcom/supporters/volunteers.ts';
import { getActiveVolunteerSlots } from '@sola/shared-utils/supporters/volunteers.ts';
import { submitVolunteerRegistration } from '@sola/skippy-sdk/requests.ts';
import { reindexVolunteerLocation, useLiveVolunteerSlotsQuery } from '@sola/skippy-search-sdk/lib/queries.ts';
import type { LiveSlotDataDto } from '@sola/skippy-search-sdk/lib/schema.ts';
import { QUERY_KEYS } from '@sola/supporter-sdk/constants.ts';
import { useSupporterQuery } from '@sola/supporter-sdk/hooks/base.ts';
import { supporterAddressQuery, supporterContactsQuery, supporterDetailsQuery, volunteerEventsQuery } from '@sola/supporter-sdk/queries.ts';
import type { VolunteerLocationInfo, VolunteerTrackingParams } from '@sola/tracking/lib/analytics/mappings-volunteer.ts';
import type { CommittedSlot, FormError, GuestConflict, GuestContact, PreviousRegistrations, RawSlot, ShiftAssignments, VolunteerContact, VolunteerSlot } from '../../utils/volunteer-utils.ts';
import { buildPreviousRegistrations, buildSlotGroupIds, deduplicateSlots, getContactDisplayName, getContactShifts, getRelationshipApiLabel, getSameDayConflictGroups, getSlotGroupKey, isGuestContact, isMinor, mapContacts, reconcileAssignmentsToPool, validateNewGuest } from '../../utils/volunteer-utils.ts';
import { SessionExpiredError, ShiftFullError, SlotConflictError, isEventFullError } from './errors/volunteer-conflict-errors.ts';
import { resolveSubmitConflicts } from './errors/volunteer-conflict-resolution.ts';

// ── Types & Interfaces ───────────────────────────────────────────────────────

interface WaiverSignature {
  contactId: string;
  name: string;
  dateSigned: string;
  accepted: boolean;
}
export interface RegistrationState {
  selectedContactIds: string[];
  guests: GuestContact[];
  isAddingGuest: boolean;
  assignments: ShiftAssignments;
  waivers: WaiverSignature[];
  waiverAgreed: boolean;
  waiverTextOptIn: boolean;
  waiverSignerId: string;
}
export type RegistrationAction = {
  type: 'TOGGLE_CONTACT';
  contactId: string;
} | {
  type: 'ADD_GUEST';
  guest: GuestContact;
} | {
  type: 'REMOVE_GUEST';
  guestId: string;
} | {
  type: 'SET_ADDING_GUEST';
  value: boolean;
} | {
  type: 'SET_ASSIGNMENTS';
  assignments: ShiftAssignments;
} | {
  type: 'SIGN_WAIVER';
  contactId: string;
  name: string;
} | {
  type: 'SET_WAIVER_AGREED';
  value: boolean;
} | {
  type: 'SET_WAIVER_TEXT_OPT_IN';
  value: boolean;
} | {
  type: 'SET_WAIVER_SIGNER_ID';
  value: string;
} | {
  type: 'INVALIDATE_WAIVERS';
} | {
  type: 'RESET';
};

// ── Pure Utility Functions ───────────────────────────────────────────────────

/**
 * Re-maps each assignment to a random available slot in the same group.
 * Throws {@link SlotConflictError} if a group is gone (nothing to remap to),
 * or {@link ShiftFullError} if a group is full (every seat claimed, so the
 * user must remove someone or pick another shift rather than retry).
 *
 * Groups are resolved from `previousSlots` (what the user selected from) so a
 * picked ID consumed during refresh can still reassign within its group.
 * Falls back to `allSlots` when omitted (only safe if every ID still exists).
 *
 * `unavailableSlotIds` are seats the backend already rejected on a prior submit
 * (AlreadyBooked/SlotNotFound). They're excluded from the remap pool so a retry
 * can't pick them again — this is what breaks the conflict loop when the live
 * feed still lists a seat the booking backend considers taken. When excluding
 * them empties a group, {@link ShiftFullError} is thrown as usual.
 */
export const reassignFreshSlots = (currentAssignments: ShiftAssignments, allSlots: RawSlot[], previousSlots: RawSlot[] = allSlots, unavailableSlotIds: ReadonlySet<string> = new Set()): ShiftAssignments => {
  // Fresh pool from live data
  const freshPool = buildSlotGroupIds(allSlots);

  // Seats the backend already rejected (AlreadyBooked/SlotNotFound) are treated
  // as claimed up front, so a remap can't re-pick them. The live-slots feed is
  // a search index that lags real bookings, so it can still list these IDs; the
  // conflict responses are the authoritative signal that they're gone.
  const claimed = new Set<string>(unavailableSlotIds);

  // Old slot ID -> group key, from pre-refresh slots the user selected.
  const slotIdToGroupKey = new Map<string, string>();
  for (const slot of previousSlots) {
    slotIdToGroupKey.set(slot.id, getSlotGroupKey(slot));
  }

  // Collect every full group across all assignments (rather than throwing on
  // the first) so the UI can highlight all affected shifts at once. Preserves
  // insertion order and de-dupes.
  const fullGroupKeys: string[] = [];
  const markFull = (groupKey: string) => {
    if (!fullGroupKeys.includes(groupKey)) {
      fullGroupKeys.push(groupKey);
    }
  };
  const newAssignments = Object.entries(currentAssignments).reduce<ShiftAssignments>((acc, [contactId, slotIds]) => {
    const freshIds: string[] = [];
    for (const oldId of slotIds) {
      const groupKey = slotIdToGroupKey.get(oldId);
      if (!groupKey) {
        throw new SlotConflictError([`Slot "${oldId}" is no longer available.`]);
      }
      const pool = freshPool.get(groupKey) ?? [];
      const available = pool.filter(id => !claimed.has(id));
      if (available.length === 0) {
        // Group exists but every seat is claimed: nothing to remap to. Record
        // it (don't pick) and keep scanning so all full groups are reported.
        markFull(groupKey);
        continue;
      }
      const picked = available[Math.floor(Math.random() * available.length)];
      // Claim the picked slot so it can't be reused by another contact.
      claimed.add(picked);
      freshIds.push(picked);
    }
    acc[contactId] = freshIds;
    return acc;
  }, {});

  // Any full group means the submission can't be remapped as-is: the user must
  // change those shifts. Throw once with every full group so all are flagged.
  if (fullGroupKeys.length > 0) {
    throw new ShiftFullError(fullGroupKeys.map(key => `No available slots left in group "${key}".`), fullGroupKeys);
  }
  return newAssignments;
};

/**
 * Build the registration payload from the current state.
 * Extracted as a pure function for testability.
 */
export const buildRegistrationPayload = (opts: {
  registrants: VolunteerContact[];
  assignments: ShiftAssignments;
  waivers: WaiverSignature[];
  postalCode: string | null;
}): VolunteerRegistrationPayload => {
  const {
    registrants,
    assignments,
    waivers,
    postalCode
  } = opts;
  const payloadSlots: VolunteerRegistrationPayload['slots'] = [];
  for (const contact of registrants) {
    const slotIds = assignments[contact.id] ?? [];
    if (slotIds.length === 0) {
      continue;
    }
    const isWaiverComplete = waivers.some(w => w.contactId === contact.id && w.accepted);
    const isGuest = isGuestContact(contact);
    const guestData = isGuest ? {
      firstName: contact.firstName,
      lastName: contact.lastName,
      email: contact.email,
      phone: contact.phone,
      zipcode: postalCode,
      isMinor: isMinor(contact.ageRange),
      relationship: getRelationshipApiLabel(contact.relationship)
    } : undefined;
    for (const slotId of slotIds) {
      if (isGuest && guestData) {
        payloadSlots.push({
          slotId,
          guest: guestData,
          isWaiverComplete
        });
      } else {
        payloadSlots.push({
          slotId,
          contactId: contact.id,
          isWaiverComplete
        });
      }
    }
  }
  return {
    slots: payloadSlots
  };
};

// ── Reducer ──────────────────────────────────────────────────────────────────

export const initialState: RegistrationState = {
  selectedContactIds: [],
  guests: [],
  isAddingGuest: false,
  assignments: {},
  waivers: [],
  waiverAgreed: false,
  waiverTextOptIn: false,
  waiverSignerId: ''
};
export const registrationReducer = (state: RegistrationState, action: RegistrationAction): RegistrationState => {
  switch (action.type) {
    case 'TOGGLE_CONTACT':
      {
        const removing = state.selectedContactIds.includes(action.contactId);
        const ids = removing ? state.selectedContactIds.filter(id => id !== action.contactId) : [...state.selectedContactIds, action.contactId];
        if (removing) {
          const {
            [action.contactId]: _,
            ...remainingAssignments
          } = state.assignments;
          return {
            ...state,
            selectedContactIds: ids,
            assignments: remainingAssignments,
            waivers: [],
            waiverAgreed: false,
            waiverSignerId: ''
          };
        }
        return {
          ...state,
          selectedContactIds: ids
        };
      }
    case 'ADD_GUEST':
      {
        return {
          ...state,
          guests: [...state.guests, action.guest],
          selectedContactIds: [...state.selectedContactIds, action.guest.id],
          isAddingGuest: false,
          waivers: [],
          waiverAgreed: false,
          waiverSignerId: ''
        };
      }
    case 'REMOVE_GUEST':
      {
        const {
          [action.guestId]: _,
          ...remainingAssignments
        } = state.assignments;
        return {
          ...state,
          guests: state.guests.filter(g => g.id !== action.guestId),
          selectedContactIds: state.selectedContactIds.filter(id => id !== action.guestId),
          assignments: remainingAssignments,
          waivers: [],
          waiverAgreed: false,
          waiverSignerId: ''
        };
      }
    case 'SET_ADDING_GUEST':
      {
        return {
          ...state,
          isAddingGuest: action.value
        };
      }
    case 'SET_ASSIGNMENTS':
      {
        return {
          ...state,
          assignments: action.assignments,
          waivers: [],
          waiverAgreed: false,
          waiverSignerId: ''
        };
      }
    case 'SIGN_WAIVER':
      {
        const filtered = state.waivers.filter(w => w.contactId !== action.contactId);
        return {
          ...state,
          waivers: [...filtered, {
            contactId: action.contactId,
            name: action.name,
            dateSigned: new Date().toISOString(),
            accepted: true
          }]
        };
      }
    case 'SET_WAIVER_AGREED':
      {
        return {
          ...state,
          waiverAgreed: action.value
        };
      }
    case 'SET_WAIVER_TEXT_OPT_IN':
      {
        return {
          ...state,
          waiverTextOptIn: action.value
        };
      }
    case 'SET_WAIVER_SIGNER_ID':
      {
        return {
          ...state,
          waiverSignerId: action.value
        };
      }
    case 'INVALIDATE_WAIVERS':
      {
        return {
          ...state,
          waivers: [],
          waiverAgreed: false,
          waiverSignerId: ''
        };
      }
    case 'RESET':
      {
        return initialState;
      }
    default:
      {
        return state;
      }
  }
};

// ── Constants ────────────────────────────────────────────────────────────────

const stepCount = 4;

/** Merge new per-contact group locks into a locks state map (de-duped). */
const mergeLocks = (setLocks: Dispatch<SetStateAction<Record<string, string[]>>>, newLocks: Record<string, string[]>) => {
  if (Object.keys(newLocks).length === 0) {
    return;
  }
  setLocks(prev => {
    const merged: Record<string, string[]> = {
      ...prev
    };
    for (const [contactId, groupKeys] of Object.entries(newLocks)) {
      merged[contactId] = [...new Set([...(merged[contactId] ?? []), ...groupKeys])];
    }
    return merged;
  });
};

// ── Hook ─────────────────────────────────────────────────────────────────────

export const useVolunteerRegistrationSheet = eventId => {
  const $ = _c(145);
  const [step, setStepState] = useState(0);
  const queryClient = useQueryClient();
  const [state, dispatch] = useReducer(registrationReducer, initialState);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = [];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const [formErrors, setFormErrors] = useState(t0);
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => setFormErrors([]);
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const clearFormErrors = t1;
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = nextStep => {
      clearFormErrors();
      setStepState(nextStep);
    };
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const setStep = t2;
  const [unavailableSlotIds, setUnavailableSlotIds] = useState(_temp);
  const [fullShiftGroupKeys, setFullShiftGroupKeys] = useState(_temp2);
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {};
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const [conflictRegistrations, setConflictRegistrations] = useState(t3);
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {};
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const [sameDayRegistrations, setSameDayRegistrations] = useState(t4);
  const {
    data: liveSlots,
    isPending: slotsPending,
    isError: slotsError
  } = useLiveVolunteerSlotsQuery(eventId);
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = [{
      ...volunteerEventsQuery,
      waitForPrimer: false
    }, {
      ...supporterContactsQuery,
      waitForPrimer: false
    }, {
      ...supporterAddressQuery,
      waitForPrimer: false
    }, {
      ...supporterDetailsQuery,
      waitForPrimer: false
    }];
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  const {
    data: t6,
    isPending: contactsPending,
    isError: contactsError
  } = useSupporterQuery(t5);
  const [volunteerEvents, rawContacts, rawAddress, rawDetails] = t6;
  let assignments;
  let confirmationShiftsByContact;
  let contacts;
  let contactsWithoutShifts;
  let duplicateShiftGroupKeys;
  let getLockedGroupKeys;
  let hasAnyShiftsAssigned;
  let hasExistingRegistrations;
  let isLastStep;
  let previousRegistrations;
  let previouslyRegisteredContactIds;
  let registrants;
  let sameDayRegistrationsMerged;
  let sameDayShiftGroupKeys;
  let selectedContacts;
  let slotGroupIds;
  let slots;
  let supporterAddress;
  let supporterName;
  let t7;
  let totalAvailableSlots;
  if ($[6] !== conflictRegistrations || $[7] !== eventId || $[8] !== liveSlots?.slots || $[9] !== rawAddress || $[10] !== rawContacts || $[11] !== rawDetails?.preferredName || $[12] !== sameDayRegistrations || $[13] !== state.assignments || $[14] !== state.guests || $[15] !== state.selectedContactIds || $[16] !== step || $[17] !== unavailableSlotIds || $[18] !== volunteerEvents) {
    const confirmationVolunteerEvent = volunteerEvents?.find(event => event.id === eventId);
    const {
      previousRegistrations: backendRegistrations,
      previouslyRegisteredContactIds: t8
    } = buildPreviousRegistrations(getActiveVolunteerSlots(confirmationVolunteerEvent?.slots ?? []));
    previouslyRegisteredContactIds = t8;
    let t9;
    if ($[40] !== conflictRegistrations) {
      t9 = new Set(Object.values(conflictRegistrations).flat());
      $[40] = conflictRegistrations;
      $[41] = t9;
    } else {
      t9 = $[41];
    }
    duplicateShiftGroupKeys = t9;
    supporterName = rawDetails?.preferredName;
    supporterAddress = rawAddress ?? null;
    const knownContacts = mapContacts(rawContacts ?? null);
    contacts = [...knownContacts, ...state.guests];
    let t10;
    if ($[42] !== unavailableSlotIds) {
      t10 = slot => !unavailableSlotIds.has(slot.id);
      $[42] = unavailableSlotIds;
      $[43] = t10;
    } else {
      t10 = $[43];
    }
    const availableRawSlots = ((liveSlots?.slots ?? []) as RawSlot[]).filter(t10);
    slots = deduplicateSlots(availableRawSlots);
    slotGroupIds = buildSlotGroupIds(availableRawSlots);
    const liveSlotIds = new Set(availableRawSlots.map(_temp3));
    assignments = reconcileAssignmentsToPool(state.assignments, liveSlotIds);
    isLastStep = step === stepCount - 1;
    totalAvailableSlots = slots.reduce(_temp4, 0);
    selectedContacts = contacts.filter(c => state.selectedContactIds.includes(c.id));
    let t11;
    if ($[44] !== eventId) {
      t11 = event_0 => event_0.id !== eventId;
      $[44] = eventId;
      $[45] = t11;
    } else {
      t11 = $[45];
    }
    const otherEventCommitments = (volunteerEvents ?? []).filter(t11).flatMap(_temp6);
    const preemptiveSameDayGroups = getSameDayConflictGroups(slots, otherEventCommitments, state.selectedContactIds);
    sameDayRegistrationsMerged = {
      ...sameDayRegistrations
    };
    for (const [contactId, groupKeys] of Object.entries(preemptiveSameDayGroups)) {
      sameDayRegistrationsMerged[contactId] = [...new Set([...(sameDayRegistrationsMerged[contactId] ?? []), ...groupKeys])];
    }
    previousRegistrations = {
      ...backendRegistrations
    };
    for (const [contactId_0, groupKeys_0] of [...Object.entries(conflictRegistrations), ...Object.entries(sameDayRegistrationsMerged)]) {
      previousRegistrations[contactId_0] = [...new Set([...(previousRegistrations[contactId_0] ?? []), ...groupKeys_0])];
    }
    sameDayShiftGroupKeys = new Set(Object.values(sameDayRegistrationsMerged).flat());
    let t12;
    if ($[46] !== conflictRegistrations || $[47] !== previouslyRegisteredContactIds || $[48] !== sameDayRegistrationsMerged) {
      t12 = previouslyRegisteredContactIds.size > 0 || Object.keys(conflictRegistrations).length > 0 || Object.keys(sameDayRegistrationsMerged).length > 0;
      $[46] = conflictRegistrations;
      $[47] = previouslyRegisteredContactIds;
      $[48] = sameDayRegistrationsMerged;
      $[49] = t12;
    } else {
      t12 = $[49];
    }
    hasExistingRegistrations = t12;
    let t13;
    if ($[50] !== previousRegistrations) {
      t13 = contactId_1 => previousRegistrations[contactId_1] ?? [];
      $[50] = previousRegistrations;
      $[51] = t13;
    } else {
      t13 = $[51];
    }
    getLockedGroupKeys = t13;
    registrants = selectedContacts.filter(c_0 => (state.assignments[c_0.id]?.length ?? 0) > 0);
    const rawSlots = (liveSlots?.slots ?? []) as RawSlot[];
    confirmationShiftsByContact = Object.fromEntries(registrants.map(c_1 => [c_1.id, getContactShifts(state.assignments[c_1.id] ?? [], rawSlots)]));
    contactsWithoutShifts = selectedContacts.filter(c_2 => (assignments[c_2.id]?.length ?? 0) === 0);
    hasAnyShiftsAssigned = selectedContacts.some(c_3 => (assignments[c_3.id]?.length ?? 0) > 0);
    t7 = selectedContacts.filter(_temp7);
    $[6] = conflictRegistrations;
    $[7] = eventId;
    $[8] = liveSlots?.slots;
    $[9] = rawAddress;
    $[10] = rawContacts;
    $[11] = rawDetails?.preferredName;
    $[12] = sameDayRegistrations;
    $[13] = state.assignments;
    $[14] = state.guests;
    $[15] = state.selectedContactIds;
    $[16] = step;
    $[17] = unavailableSlotIds;
    $[18] = volunteerEvents;
    $[19] = assignments;
    $[20] = confirmationShiftsByContact;
    $[21] = contacts;
    $[22] = contactsWithoutShifts;
    $[23] = duplicateShiftGroupKeys;
    $[24] = getLockedGroupKeys;
    $[25] = hasAnyShiftsAssigned;
    $[26] = hasExistingRegistrations;
    $[27] = isLastStep;
    $[28] = previousRegistrations;
    $[29] = previouslyRegisteredContactIds;
    $[30] = registrants;
    $[31] = sameDayRegistrationsMerged;
    $[32] = sameDayShiftGroupKeys;
    $[33] = selectedContacts;
    $[34] = slotGroupIds;
    $[35] = slots;
    $[36] = supporterAddress;
    $[37] = supporterName;
    $[38] = t7;
    $[39] = totalAvailableSlots;
  } else {
    assignments = $[19];
    confirmationShiftsByContact = $[20];
    contacts = $[21];
    contactsWithoutShifts = $[22];
    duplicateShiftGroupKeys = $[23];
    getLockedGroupKeys = $[24];
    hasAnyShiftsAssigned = $[25];
    hasExistingRegistrations = $[26];
    isLastStep = $[27];
    previousRegistrations = $[28];
    previouslyRegisteredContactIds = $[29];
    registrants = $[30];
    sameDayRegistrationsMerged = $[31];
    sameDayShiftGroupKeys = $[32];
    selectedContacts = $[33];
    slotGroupIds = $[34];
    slots = $[35];
    supporterAddress = $[36];
    supporterName = $[37];
    t7 = $[38];
    totalAvailableSlots = $[39];
  }
  const eligibleSigners = t7;
  const hasAdultSelected = eligibleSigners.length > 0;
  const hasPeopleLoadError = contactsError || slotsError;
  let t8;
  bb0: switch (step) {
    case 0:
      {
        t8 = !hasPeopleLoadError && state.selectedContactIds.length > 0 && !state.isAddingGuest && hasAdultSelected;
        break bb0;
      }
    case 1:
      {
        t8 = hasAnyShiftsAssigned;
        break bb0;
      }
    case 2:
      {
        t8 = state.waiverAgreed;
        break bb0;
      }
    case 3:
      {
        t8 = true;
        break bb0;
      }
    default:
      {
        t8 = false;
      }
  }
  const canAdvance = t8;
  let t9;
  if ($[52] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = contactId_2 => {
      dispatch({
        type: "TOGGLE_CONTACT",
        contactId: contactId_2
      });
    };
    $[52] = t9;
  } else {
    t9 = $[52];
  }
  const toggleContact = t9;
  let t10;
  if ($[53] !== contacts) {
    t10 = guest => {
      const conflict = validateNewGuest(guest, contacts);
      if (conflict) {
        return conflict;
      }
      dispatch({
        type: "ADD_GUEST",
        guest
      });
      return null;
    };
    $[53] = contacts;
    $[54] = t10;
  } else {
    t10 = $[54];
  }
  const addGuest = t10;
  let t11;
  if ($[55] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = guestId => {
      dispatch({
        type: "REMOVE_GUEST",
        guestId
      });
    };
    $[55] = t11;
  } else {
    t11 = $[55];
  }
  const removeGuest = t11;
  let t12;
  if ($[56] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = value => {
      dispatch({
        type: "SET_ADDING_GUEST",
        value
      });
    };
    $[56] = t12;
  } else {
    t12 = $[56];
  }
  const setIsAddingGuest = t12;
  let t13;
  if ($[57] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = (contactId_3, name) => {
      dispatch({
        type: "SIGN_WAIVER",
        contactId: contactId_3,
        name
      });
    };
    $[57] = t13;
  } else {
    t13 = $[57];
  }
  const signWaiver = t13;
  let t14;
  if ($[58] !== eventId || $[59] !== queryClient) {
    t14 = () => {
      setUnavailableSlotIds(new Set());
      setFullShiftGroupKeys(new Set());
      queryClient.invalidateQueries({
        queryKey: ["volunteerLiveSlots", eventId]
      });
    };
    $[58] = eventId;
    $[59] = queryClient;
    $[60] = t14;
  } else {
    t14 = $[60];
  }
  const refreshSlots = t14;
  let t15;
  if ($[61] !== getLockedGroupKeys || $[62] !== slotGroupIds || $[63] !== slots || $[64] !== state.assignments) {
    t15 = (contactId_4, groupSlotId) => {
      const current = state.assignments[contactId_4] ?? [];
      const slot_2 = slots.find(s_0 => s_0.id === groupSlotId);
      if (!slot_2) {
        return;
      }
      const groupKey = getSlotGroupKey(slot_2);
      const pool = slotGroupIds.get(groupKey) ?? [];
      const existingId = current.find(id => pool.includes(id));
      if (existingId) {
        const lockedGroupKeys = getLockedGroupKeys(contactId_4);
        if (lockedGroupKeys.includes(groupKey)) {
          return;
        }
        dispatch({
          type: "SET_ASSIGNMENTS",
          assignments: {
            ...state.assignments,
            [contactId_4]: current.filter(id_0 => id_0 !== existingId)
          }
        });
        return;
      }
      const allAssignedIds = new Set(Object.values(state.assignments).flat());
      const freeId = pool.find(id_1 => !allAssignedIds.has(id_1));
      if (!freeId) {
        return;
      }
      dispatch({
        type: "SET_ASSIGNMENTS",
        assignments: {
          ...state.assignments,
          [contactId_4]: [...current, freeId]
        }
      });
    };
    $[61] = getLockedGroupKeys;
    $[62] = slotGroupIds;
    $[63] = slots;
    $[64] = state.assignments;
    $[65] = t15;
  } else {
    t15 = $[65];
  }
  const toggleSlot = t15;
  let t16;
  if ($[66] !== getLockedGroupKeys || $[67] !== slotGroupIds || $[68] !== slots || $[69] !== state.assignments) {
    t16 = (groupSlotId_0, assignedContactIds) => {
      const slot_3 = slots.find(s_1 => s_1.id === groupSlotId_0);
      if (!slot_3) {
        return;
      }
      const groupKey_0 = getSlotGroupKey(slot_3);
      const pool_0 = slotGroupIds.get(groupKey_0) ?? [];
      const nextAssignments = {
        ...state.assignments
      };
      const usedPoolIds = new Set();
      for (const [cId, ids] of Object.entries(nextAssignments)) {
        if (!assignedContactIds.includes(cId)) {
          const isLockedForThisGroup = getLockedGroupKeys(cId).includes(groupKey_0);
          if (isLockedForThisGroup) {
            for (const id_2 of ids) {
              if (pool_0.includes(id_2)) {
                usedPoolIds.add(id_2);
              }
            }
          }
        }
      }
      for (const contactId_5 of assignedContactIds) {
        const current_0 = nextAssignments[contactId_5] ?? [];
        const existingId_0 = current_0.find(id_3 => pool_0.includes(id_3));
        if (existingId_0) {
          usedPoolIds.add(existingId_0);
          continue;
        }
        const freeId_0 = pool_0.find(id_4 => !usedPoolIds.has(id_4));
        if (freeId_0) {
          usedPoolIds.add(freeId_0);
          nextAssignments[contactId_5] = [...current_0, freeId_0];
        }
      }
      for (const [cId_0, ids_0] of Object.entries(nextAssignments)) {
        if (!assignedContactIds.includes(cId_0)) {
          const isLockedForThisGroup_0 = getLockedGroupKeys(cId_0).includes(groupKey_0);
          const filtered = ids_0.filter(id_5 => !pool_0.includes(id_5) || isLockedForThisGroup_0);
          if (filtered.length !== ids_0.length) {
            nextAssignments[cId_0] = filtered;
          }
        }
      }
      dispatch({
        type: "SET_ASSIGNMENTS",
        assignments: nextAssignments
      });
    };
    $[66] = getLockedGroupKeys;
    $[67] = slotGroupIds;
    $[68] = slots;
    $[69] = state.assignments;
    $[70] = t16;
  } else {
    t16 = $[70];
  }
  const setSlotAssignments = t16;
  let t17;
  if ($[71] !== assignments || $[72] !== registrants || $[73] !== state.waivers || $[74] !== supporterAddress?.postalCode) {
    t17 = assignmentOverrides => buildRegistrationPayload({
      registrants,
      assignments: assignmentOverrides ?? assignments,
      waivers: state.waivers,
      postalCode: supporterAddress?.postalCode ?? null
    });
    $[71] = assignments;
    $[72] = registrants;
    $[73] = state.waivers;
    $[74] = supporterAddress?.postalCode;
    $[75] = t17;
  } else {
    t17 = $[75];
  }
  const buildPayload = t17;
  let t18;
  if ($[76] !== buildPayload || $[77] !== contacts || $[78] !== eligibleSigners[0] || $[79] !== eventId || $[80] !== isLastStep || $[81] !== liveSlots?.slots || $[82] !== queryClient || $[83] !== refreshSlots || $[84] !== registrants || $[85] !== selectedContacts || $[86] !== state.assignments || $[87] !== state.waiverAgreed || $[88] !== state.waiverSignerId || $[89] !== step || $[90] !== unavailableSlotIds) {
    t18 = async onComplete => {
      if (isLastStep) {
        await queryClient.refetchQueries({
          queryKey: ["volunteerLiveSlots", eventId]
        });
        const freshLiveSlots = queryClient.getQueryData(["volunteerLiveSlots", eventId]);
        const freshRawSlots = (freshLiveSlots?.slots ?? liveSlots?.slots ?? []) as RawSlot[];
        const previousRawSlots = (liveSlots?.slots ?? []) as RawSlot[];
        let freshAssignments;
        ;
        try {
          freshAssignments = reassignFreshSlots(state.assignments, freshRawSlots, previousRawSlots, unavailableSlotIds);
        } catch (t19) {
          const error = t19;
          if (error instanceof ShiftFullError) {
            setFullShiftGroupKeys(new Set(error.fullGroupKeys));
          }
          throw error;
        }
        setFullShiftGroupKeys(new Set());
        const payload = buildPayload(freshAssignments);
        const result = await submitVolunteerRegistration(payload);
        if (result.sessionExpired) {
          throw new SessionExpiredError();
        }
        if (!result.success) {
          const errors = result.errors ?? [];
          const resolution = resolveSubmitConflicts({
            errors,
            stateAssignments: state.assignments,
            freshAssignments,
            freshRawSlots,
            previousRawSlots,
            unavailableSlotIds,
            contacts
          });
          if (resolution.rejectedSlotIds.length > 0) {
            setUnavailableSlotIds(prev => new Set([...prev, ...resolution.rejectedSlotIds]));
          }
          mergeLocks(setConflictRegistrations, resolution.duplicateLocks);
          mergeLocks(setSameDayRegistrations, resolution.sameDayLocks);
          setFullShiftGroupKeys(new Set(resolution.fullGroupKeys));
          if (resolution.nextAssignments !== state.assignments) {
            dispatch({
              type: "SET_ASSIGNMENTS",
              assignments: resolution.nextAssignments
            });
          }
          await queryClient.refetchQueries({
            queryKey: ["volunteerLiveSlots", eventId]
          });
          if (isEventFullError(resolution.error)) {
            reindexVolunteerLocation(eventId).catch(_temp8);
          }
          throw resolution.error;
        }
        reindexVolunteerLocation(eventId).catch(_temp9);
        queryClient.invalidateQueries({
          queryKey: ["volunteerLiveSlots", eventId]
        });
        queryClient.invalidateQueries({
          queryKey: [QUERY_KEYS.supporterVolunteerEvents]
        });
        queryClient.invalidateQueries({
          queryKey: [QUERY_KEYS.supporterContacts]
        });
        onComplete?.();
        return;
      }
      const nextStep_0 = step + 1;
      if (nextStep_0 === 1) {
        refreshSlots();
      }
      if (step === 2 && state.waiverAgreed) {
        const effectiveSignerId = state.waiverSignerId || eligibleSigners[0]?.id;
        const signer = selectedContacts.find(c_5 => c_5.id === effectiveSignerId) ?? eligibleSigners[0];
        const signerName = signer ? getContactDisplayName(signer, {
          variant: "full"
        }) : "";
        for (const contact of registrants) {
          const isSignerContact = contact.id === effectiveSignerId;
          const isMinorGuest = isGuestContact(contact) && isMinor(contact.ageRange);
          if (isSignerContact || isMinorGuest) {
            signWaiver(contact.id, signerName);
          }
        }
      }
      setStep(nextStep_0);
    };
    $[76] = buildPayload;
    $[77] = contacts;
    $[78] = eligibleSigners[0];
    $[79] = eventId;
    $[80] = isLastStep;
    $[81] = liveSlots?.slots;
    $[82] = queryClient;
    $[83] = refreshSlots;
    $[84] = registrants;
    $[85] = selectedContacts;
    $[86] = state.assignments;
    $[87] = state.waiverAgreed;
    $[88] = state.waiverSignerId;
    $[89] = step;
    $[90] = unavailableSlotIds;
    $[91] = t18;
  } else {
    t18 = $[91];
  }
  const handleNext = t18;
  let t19;
  if ($[92] !== step) {
    t19 = () => {
      setStep(step - 1);
    };
    $[92] = step;
    $[93] = t19;
  } else {
    t19 = $[93];
  }
  const handleBack = t19;
  let t20;
  if ($[94] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = () => {
      setStep(0);
      dispatch({
        type: "RESET"
      });
      setUnavailableSlotIds(new Set());
      setFullShiftGroupKeys(new Set());
      setConflictRegistrations({});
      setSameDayRegistrations({});
    };
    $[94] = t20;
  } else {
    t20 = $[94];
  }
  const resetFlow = t20;
  let t21;
  if ($[95] !== assignments || $[96] !== slots || $[97] !== state.selectedContactIds) {
    t21 = (stepIndex, location) => ({
      stepIndex,
      location,
      slots,
      assignments,
      selectedContactIds: state.selectedContactIds,
      isTour: (location.tourCount ?? 0) > 1
    });
    $[95] = assignments;
    $[96] = slots;
    $[97] = state.selectedContactIds;
    $[98] = t21;
  } else {
    t21 = $[98];
  }
  const buildTrackingParams = t21;
  const t22 = state.waiverSignerId || eligibleSigners[0]?.id || "";
  let t23;
  let t24;
  let t25;
  if ($[99] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = value_0 => dispatch({
      type: "SET_WAIVER_AGREED",
      value: value_0
    });
    t24 = value_1 => dispatch({
      type: "SET_WAIVER_TEXT_OPT_IN",
      value: value_1
    });
    t25 = value_2 => dispatch({
      type: "SET_WAIVER_SIGNER_ID",
      value: value_2
    });
    $[99] = t23;
    $[100] = t24;
    $[101] = t25;
  } else {
    t23 = $[99];
    t24 = $[100];
    t25 = $[101];
  }
  let t26;
  if ($[102] !== addGuest || $[103] !== assignments || $[104] !== buildPayload || $[105] !== buildTrackingParams || $[106] !== canAdvance || $[107] !== confirmationShiftsByContact || $[108] !== contacts || $[109] !== contactsError || $[110] !== contactsPending || $[111] !== contactsWithoutShifts || $[112] !== duplicateShiftGroupKeys || $[113] !== formErrors || $[114] !== fullShiftGroupKeys || $[115] !== handleBack || $[116] !== handleNext || $[117] !== hasAdultSelected || $[118] !== hasExistingRegistrations || $[119] !== isLastStep || $[120] !== previousRegistrations || $[121] !== previouslyRegisteredContactIds || $[122] !== refreshSlots || $[123] !== registrants || $[124] !== sameDayRegistrationsMerged || $[125] !== sameDayShiftGroupKeys || $[126] !== selectedContacts || $[127] !== setSlotAssignments || $[128] !== slotGroupIds || $[129] !== slots || $[130] !== slotsError || $[131] !== slotsPending || $[132] !== state.guests || $[133] !== state.isAddingGuest || $[134] !== state.selectedContactIds || $[135] !== state.waiverAgreed || $[136] !== state.waiverTextOptIn || $[137] !== state.waivers || $[138] !== step || $[139] !== supporterAddress || $[140] !== supporterName || $[141] !== t22 || $[142] !== toggleSlot || $[143] !== totalAvailableSlots) {
    t26 = {
      step,
      contacts,
      guests: state.guests,
      isAddingGuest: state.isAddingGuest,
      slots,
      slotGroupIds,
      fullShiftGroupKeys,
      duplicateShiftGroupKeys,
      sameDayShiftGroupKeys,
      sameDayRegistrations: sameDayRegistrationsMerged,
      totalAvailableSlots,
      supporterName,
      supporterAddress,
      selectedContactIds: state.selectedContactIds,
      selectedContacts,
      registrants,
      contactsWithoutShifts,
      assignments,
      confirmationShiftsByContact,
      previousRegistrations,
      previouslyRegisteredContactIds,
      hasExistingRegistrations,
      waivers: state.waivers,
      waiverAgreed: state.waiverAgreed,
      waiverTextOptIn: state.waiverTextOptIn,
      waiverSignerId: t22,
      isLastStep,
      canAdvance,
      hasAdultSelected,
      contactsPending,
      contactsError,
      slotsPending,
      slotsError,
      formErrors,
      setFormErrors,
      clearFormErrors,
      setStep,
      toggleContact,
      addGuest,
      removeGuest,
      setIsAddingGuest,
      toggleSlot,
      setSlotAssignments,
      signWaiver,
      setWaiverAgreed: t23,
      setWaiverTextOptIn: t24,
      setWaiverSignerId: t25,
      refreshSlots,
      handleNext,
      handleBack,
      resetFlow,
      buildPayload,
      buildTrackingParams
    };
    $[102] = addGuest;
    $[103] = assignments;
    $[104] = buildPayload;
    $[105] = buildTrackingParams;
    $[106] = canAdvance;
    $[107] = confirmationShiftsByContact;
    $[108] = contacts;
    $[109] = contactsError;
    $[110] = contactsPending;
    $[111] = contactsWithoutShifts;
    $[112] = duplicateShiftGroupKeys;
    $[113] = formErrors;
    $[114] = fullShiftGroupKeys;
    $[115] = handleBack;
    $[116] = handleNext;
    $[117] = hasAdultSelected;
    $[118] = hasExistingRegistrations;
    $[119] = isLastStep;
    $[120] = previousRegistrations;
    $[121] = previouslyRegisteredContactIds;
    $[122] = refreshSlots;
    $[123] = registrants;
    $[124] = sameDayRegistrationsMerged;
    $[125] = sameDayShiftGroupKeys;
    $[126] = selectedContacts;
    $[127] = setSlotAssignments;
    $[128] = slotGroupIds;
    $[129] = slots;
    $[130] = slotsError;
    $[131] = slotsPending;
    $[132] = state.guests;
    $[133] = state.isAddingGuest;
    $[134] = state.selectedContactIds;
    $[135] = state.waiverAgreed;
    $[136] = state.waiverTextOptIn;
    $[137] = state.waivers;
    $[138] = step;
    $[139] = supporterAddress;
    $[140] = supporterName;
    $[141] = t22;
    $[142] = toggleSlot;
    $[143] = totalAvailableSlots;
    $[144] = t26;
  } else {
    t26 = $[144];
  }
  return t26;
};
function _temp() {
  return new Set();
}
function _temp2() {
  return new Set();
}
function _temp3(slot_0) {
  return slot_0.id;
}
function _temp4(sum, s) {
  return sum + s.remainingCapacity;
}
function _temp5(slot_1) {
  return {
    contactId: slot_1.contactId,
    startDate: slot_1.startDate,
    endDate: slot_1.endDate
  };
}
function _temp6(event_1) {
  return getActiveVolunteerSlots(event_1.slots).map(_temp5);
}
function _temp7(c_4) {
  return !isGuestContact(c_4) || !isMinor(c_4.ageRange);
}
function _temp8() {}
function _temp9() {}